import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useServiceProperty } from "../../hooks/serviceProperty";
import { AppProperty } from "../../models/Property";
import Footer from "../../components/footer/footer";

export default function PropertyReport() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState<AppProperty>();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<{
        success: boolean;
        message: string;
        visible: boolean;
    }>({
        success: false,
        message: '',
        visible: false
    });
    const [isPreservedAreaChecked, setIsPreservedAreaChecked] = useState(false);
    
    const fileInputRef = useRef<HTMLInputElement>(null);

    const { getProperty, uploadEngineerReport } = useServiceProperty(id);

    useEffect(() => {
        async function fetchProperty() {
            const data = await getProperty();
            setProperty(data);
        }

        if (!property) {
            fetchProperty();
        }
    }, [property]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            if (file.type === 'application/pdf') {
                setSelectedFile(file);
                setUploadStatus({...uploadStatus, visible: false});
            } else {
                setSelectedFile(null);
                setUploadStatus({
                    success: false,
                    message: t('Apenas arquivos PDF são permitidos'),
                    visible: true
                });
            }
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleSubmit = async () => {
        if (!selectedFile) return;
        
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);
            
            await uploadEngineerReport(formData, isPreservedAreaChecked);
            
            setUploadStatus({
                success: true,
                message: t('Relatório enviado com sucesso!'),
                visible: true
            });
            setSelectedFile(null);
            
            // Limpar o input de arquivo
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        } catch (error) {
            console.error('Erro ao enviar o relatório:', error);
            setUploadStatus({
                success: false,
                message: t('Erro ao enviar o relatório. Tente novamente.'),
                visible: true
            });
        } finally {
            setIsUploading(false);
        }
    };

    return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-4 py-8">
          <div className="bg-white shadow-sm rounded-xl p-6">
            <p className="text-lg text-gray-700 !mb-6">
              {t(
                "Anexe no campo abaixo o PDF de análise técnica da propriedade, ele deverá conter todos os dados necessários para a análise técnica. Por favor, faça o link de vídeos dentro do documento via sites externos."
              )}
            </p>

            {/* Input de arquivo (oculto) */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />

            <div className="flex flex-col gap-4">
              <button
                type="button"
                onClick={handleUploadClick}
                disabled={isUploading}
                className="inline-flex items-center justify-center rounded-md !bg-green-600 !px-4 !py-2 !text-white text-sm font-medium transition-colors hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {t("Selecionar arquivo PDF")}
              </button>

              {/* Info do arquivo selecionado */}
              {selectedFile && (
                <div className="mt-1 rounded-md bg-gray-100 px-3 py-2 text-sm text-gray-800">
                  {t("Arquivo selecionado")}: {selectedFile.name}
                </div>
              )}

              {/* Checkbox: mata preservada em conformidade */}
              <label
                htmlFor="preservedCheck"
                className="flex items-start gap-3 rounded-md border border-gray-200 p-3"
              >
                <input
                  id="preservedCheck"
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-gray-300"
                  checked={isPreservedAreaChecked}
                  onChange={(e) => setIsPreservedAreaChecked(e.target.checked)}
                />
                <div className="text-sm">
                  <span className="font-medium text-gray-900">
                    {t("Confirmo que a área de mata preservada está em conformidade com o informado pelo usuário.")}
                  </span>
                  <p className="text-gray-600">
                    {t(
                      "Ao marcar esta opção, o sistema irá sinalizar a propriedade como verificada."
                    )}
                  </p>
                </div>
              </label>

              {/* Mensagem de status */}
              {uploadStatus.visible && (
                <div
                  className={`rounded-md px-3 py-2 text-sm ${
                    uploadStatus.success
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {uploadStatus.message}
                </div>
              )}

              {/* Botão de envio */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!selectedFile || isUploading || !isPreservedAreaChecked}
                className="inline-flex items-center justify-center rounded-md !bg-blue-600 !px-4 !py-2 !text-white text-sm font-medium transition-colors !hover:bg-blue-700 !disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {isUploading ? t("Enviando...") : t("Enviar relatório")}
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
    );
}
